import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type { KitchenArrivalRow } from "./RallyBoardPopup";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { FirefightClip } from "./GameStory_firefights";

import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { RallyShotQualityStrip } from "./GameStory_RallyQualityStrip";
import { RallyShotQualityMiniChart } from "./GameStory_RallyShotQualityMiniChart";
import { formatOverlayShotQuality } from "./GameStory_shotQuality";

type PlayerId = 0 | 1 | 2 | 3;
type Team01 = 0 | 1;
type TernaryFilter = "all" | "yes" | "no";

function isPlayerId(v: unknown): v is PlayerId {
  return v === 0 || v === 1 || v === 2 || v === 3;
}

function teamOfPlayer(playerId: PlayerId): Team01 {
  return playerId === 0 || playerId === 1 ? 0 : 1;
}

function getWinnerTeam(row: KitchenArrivalRow | null): Team01 | null {
  const v = row?.winnerTeam;
  return v === 0 || v === 1 ? v : null;
}

function getPointAwarded(row: KitchenArrivalRow | null): boolean | null {
  if (typeof row?.scoredPoint === "boolean") return row.scoredPoint;
  return null;
}

function getNumShots(row: KitchenArrivalRow | null): number {
  if (typeof row?.numShots === "number" && Number.isFinite(row.numShots)) {
    return row.numShots;
  }
  if (Array.isArray(row?.shotNumberOverlays)) {
    return row.shotNumberOverlays.length;
  }
  return 0;
}

function rowHasQuality(row: KitchenArrivalRow | null): boolean {
  return !!(
    row &&
    Array.isArray(row.shotNumberOverlays) &&
    row.shotNumberOverlays.some(
      (o) => typeof o?.rawShot?.quality?.overall === "number",
    )
  );
}

function rowIncludesPlayer(
  row: KitchenArrivalRow | null,
  playerId: PlayerId,
): boolean {
  if (!row || !Array.isArray(row.shotNumberOverlays)) return false;

  return row.shotNumberOverlays.some((o) => {
    const pid = o?.playerId ?? o?.rawShot?.player_id;
    return pid === playerId;
  });
}

function FilterPillButton(props: {
  label: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  bgSelected?: string;
}) {
  const { label, selected, onClick, bgSelected = "rgba(0,0,0,0.08)" } = props;

  return (
    <Button
      size="small"
      variant="text"
      onClick={onClick}
      sx={{
        textTransform: "none",
        minHeight: 24,
        py: 0,
        px: 1,
        minWidth: 0,
        fontWeight: 900,
        fontSize: 12,
        borderRadius: "999px",
        border: selected
          ? "1px solid rgba(0,0,0,0.20)"
          : "1px solid transparent",
        bgcolor: selected ? bgSelected : "white",
        boxShadow: selected ? "inset 0 0 0 1px rgba(0,0,0,0.12)" : "none",
        "&:hover": {
          borderColor: "rgba(0,0,0,0.16)",
          bgcolor: selected ? bgSelected : "rgba(0,0,0,0.03)",
        },
      }}
    >
      {label}
    </Button>
  );
}

export function RallyQualityOverviewPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  activeRallyIndex: number | null;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    activeRallyIndex,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
  } = props;

  const [currentShotNumber, setCurrentShotNumber] = React.useState<
    number | null
  >(null);

  const [selectedPlayerId, setSelectedPlayerId] =
    React.useState<PlayerId | null>(null);
  const [wonFilter, setWonFilter] = React.useState<TernaryFilter>("all");
  const [pointAwardedFilter, setPointAwardedFilter] =
    React.useState<TernaryFilter>("all");

  const selectableRows = React.useMemo(
    () => rows.filter((row) => rowHasQuality(row)),
    [rows],
  );

  const filteredRows = React.useMemo(() => {
    return selectableRows.filter((row) => {
      if (
        selectedPlayerId != null &&
        !rowIncludesPlayer(row, selectedPlayerId)
      ) {
        return false;
      }

      if (wonFilter !== "all") {
        const winner = getWinnerTeam(row);
        const selectedTeam =
          selectedPlayerId != null ? teamOfPlayer(selectedPlayerId) : null;

        if (selectedTeam == null || winner == null) return false;

        const didWin = winner === selectedTeam;
        if (wonFilter === "yes" && !didWin) return false;
        if (wonFilter === "no" && didWin) return false;
      }

      if (pointAwardedFilter !== "all") {
        const pointAwarded = getPointAwarded(row);
        if (pointAwarded == null) return false;
        if (pointAwardedFilter === "yes" && !pointAwarded) return false;
        if (pointAwardedFilter === "no" && pointAwarded) return false;
      }

      return true;
    });
  }, [selectableRows, selectedPlayerId, wonFilter, pointAwardedFilter]);

  const [selectedRallyIndex, setSelectedRallyIndex] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    if (!open) return;

    const defaultRallyIndex =
      activeRallyIndex != null &&
      filteredRows.some((r) => r.rallyIndex === activeRallyIndex)
        ? activeRallyIndex
        : (filteredRows[0]?.rallyIndex ?? null);

    setSelectedRallyIndex(defaultRallyIndex);
  }, [open, activeRallyIndex, filteredRows]);

  React.useEffect(() => {
    if (selectedRallyIndex == null) return;
    if (!filteredRows.some((r) => r.rallyIndex === selectedRallyIndex)) {
      setSelectedRallyIndex(filteredRows[0]?.rallyIndex ?? null);
    }
  }, [filteredRows, selectedRallyIndex]);

  const selectedRow = React.useMemo(
    () => rows.find((r) => r.rallyIndex === selectedRallyIndex) ?? null,
    [rows, selectedRallyIndex],
  );

  const [pos, setPos] = React.useState({ x: 18, y: 50 });
  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;

      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 240, d.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 140, d.startPosY + dy)),
      });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  React.useEffect(() => {
    setCurrentShotNumber(null);
  }, [selectedRow?.rallyIndex]);

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1380,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",
        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>Rally Quality</Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
            {filteredRows.length} rallies
          </Typography>
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Close
        </Button>
      </Box>

      <Box
        sx={{
          px: 1.2,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          bgcolor: "rgba(0,0,0,0.012)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(0,0,0,0.60)",
              mr: 0.2,
            }}
          >
            Player
          </Box>

          <FilterPillButton
            label="All"
            selected={selectedPlayerId == null}
            onClick={() => {
              setSelectedPlayerId(null);
              setWonFilter("all");
            }}
          />

          {([0, 1, 2, 3] as PlayerId[]).map((pid) => {
            const team = teamOfPlayer(pid);
            const selected = selectedPlayerId === pid;

            return (
              <Button
                key={pid}
                size="small"
                variant="text"
                onClick={() => setSelectedPlayerId(pid)}
                sx={{
                  textTransform: "none",
                  minHeight: 24,
                  py: 0,
                  px: 0.55,
                  minWidth: 0,
                  borderRadius: "999px",
                  border: selected
                    ? `1px solid ${
                        team === 0
                          ? "rgba(95,165,255,0.55)"
                          : "rgba(120,225,160,0.55)"
                      }`
                    : "1px solid transparent",
                  bgcolor: selected
                    ? team === 0
                      ? "rgba(95,165,255,0.12)"
                      : "rgba(120,225,160,0.12)"
                    : "white",
                  boxShadow: selected
                    ? "inset 0 0 0 1px rgba(0,0,0,0.05)"
                    : "none",
                  "&:hover": {
                    borderColor:
                      team === 0
                        ? "rgba(95,165,255,0.45)"
                        : "rgba(120,225,160,0.45)",
                    bgcolor: selected
                      ? team === 0
                        ? "rgba(95,165,255,0.16)"
                        : "rgba(120,225,160,0.16)"
                      : "rgba(0,0,0,0.03)",
                  },
                }}
              >
                {renderPlayerIdentity({
                  playerId: pid,
                  labelOnly: false,
                  size: 34,
                  borderRadius: 5,
                  fontSize: 12,
                })}
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            marginLeft: "12px",
            alignItems: "center",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(0,0,0,0.60)",
              mr: 0.2,
            }}
          >
            Won rally
          </Box>

          <FilterPillButton
            label="All"
            selected={wonFilter === "all"}
            onClick={() => setWonFilter("all")}
          />
          <FilterPillButton
            label="Yes"
            selected={wonFilter === "yes"}
            onClick={() => setWonFilter("yes")}
          />
          <FilterPillButton
            label="No"
            selected={wonFilter === "no"}
            onClick={() => setWonFilter("no")}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.45,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(0,0,0,0.60)",
              mr: 0.2,
            }}
          >
            Point awarded
          </Box>

          <FilterPillButton
            label="All"
            selected={pointAwardedFilter === "all"}
            onClick={() => setPointAwardedFilter("all")}
          />
          <FilterPillButton
            label="Yes"
            selected={pointAwardedFilter === "yes"}
            onClick={() => setPointAwardedFilter("yes")}
          />
          <FilterPillButton
            label="No"
            selected={pointAwardedFilter === "no"}
            onClick={() => setPointAwardedFilter("no")}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            width: 470,
            maxWidth: "48%",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
          }}
        >
          <Table size="small" stickyHeader
          sx={{
            "& .MuiTableCell-root": {verticalAlign: "bottom"}
          }}
          
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 900, width: 62 }}>Rally</TableCell>
                <TableCell sx={{ fontWeight: 900, width: 62 }} align="right">
                  Shots
                </TableCell>
                <TableCell sx={{ fontWeight: 900, width: 74 }} align="center">
                  Won Rally
                </TableCell>
                <TableCell sx={{ fontWeight: 900, width: 74 }} align="center">
                  Point Awarded
                </TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Shot Quality (Q)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => {
                const isSelected = row.rallyIndex === selectedRallyIndex;
                const winner = getWinnerTeam(row);
                const pointAwarded = getPointAwarded(row);
                const selectedTeam =
                  selectedPlayerId != null
                    ? teamOfPlayer(selectedPlayerId)
                    : null;
                const didSelectedPlayerWin =
                  selectedTeam != null && winner != null
                    ? winner === selectedTeam
                    : null;

                return (
                  <TableRow
                    key={row.rallyIndex}
                    hover
                    onClick={() => setSelectedRallyIndex(row.rallyIndex)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSelected ? "rgba(0,0,0,0.06)" : undefined,
                    }}
                  >
                    <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                      {row.rallyIndex + 1}
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {getNumShots(row)}
                    </TableCell>

                    {/* <TableCell align="center">
                      <Box
                        sx={{
                          fontSize: 11,
                          fontWeight: 900,
                          color:
                            didSelectedPlayerWin == null
                              ? "rgba(0,0,0,0.55)"
                              : didSelectedPlayerWin
                                ? "rgba(20,120,55,0.95)"
                                : "rgba(190,60,60,0.95)",
                        }}
                      >
                        {didSelectedPlayerWin == null
                          ? winner == null
                            ? "—"
                            : `T${winner}`
                          : didSelectedPlayerWin
                            ? "Yes"
                            : "No"}
                      </Box>
                    </TableCell> */}

                    <TableCell align="center">
                      {didSelectedPlayerWin == null ? (
                        winner == null ? (
                          <Box
                            sx={{
                              fontSize: 11,
                              fontWeight: 900,
                              color: "rgba(0,0,0,0.55)",
                            }}
                          >
                            —
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 0.85,
                              py: 0.2,
                              borderRadius: "999px",
                              fontSize: 10.5,
                              fontWeight: 800,
                              lineHeight: 1,
                              color:
                                winner === 0
                                  ? "rgba(35,90,180,0.96)"
                                  : "rgba(28,120,62,0.96)",
                              bgcolor:
                                winner === 0
                                  ? "rgba(95,165,255,0.16)"
                                  : "rgba(120,225,160,0.16)",
                              border: `1px solid ${
                                winner === 0
                                  ? "rgba(95,165,255,0.42)"
                                  : "rgba(120,225,160,0.42)"
                              }`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Team {winner}
                          </Box>
                        )
                      ) : (
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 900,
                            color: didSelectedPlayerWin
                              ? "rgba(20,120,55,0.95)"
                              : "rgba(190,60,60,0.95)",
                          }}
                        >
                          {didSelectedPlayerWin ? "Yes" : "No"}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          fontSize: 11,
                          fontWeight: 900,
                          color:
                            pointAwarded == null
                              ? "rgba(0,0,0,0.55)"
                              : pointAwarded
                                ? "rgba(20,120,55,0.95)"
                                : "rgba(0,0,0,0.55)",
                        }}
                      >
                        {pointAwarded == null
                          ? "—"
                          : pointAwarded
                            ? "Yes"
                            : "No"}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ minWidth: 0, width: "100%" }}>
                      <RallyShotQualityMiniChart row={row} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            {selectedRow ? (
              <Typography sx={{ fontWeight: 900 }}>
                Rally {selectedRow.rallyIndex + 1}
                {" • "}
                {getNumShots(selectedRow)} shots
              </Typography>
            ) : (
              <Typography sx={{ opacity: 0.7 }}>No rally selected.</Typography>
            )}
          </Box>

          <Box>
            <CondensedRallyTimeline_ContextOnly
              activeRally={selectedRow}
              rows={rows}
              barsAreaHeightPx={40}
              showLegend={false}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box>

          <Box sx={{ borderBottom: "1px solid rgba(0,0,0,0.10)" }}>
            <RallyShotQualityStrip
              row={selectedRow}
              renderPlayerIdentity={renderPlayerIdentity}
              currentShotNumber={currentShotNumber}
              focusShotNumber={null}
              selectedPlayerId={selectedPlayerId}
              height={82}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              bgcolor: "black",
            }}
          >
            {selectedRow ? (
              <ActiveRallyVideo
                aiContextLabel="Rally Quality"
                focusedFilter={null}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                activeShotNumber={null}
                activeShotExtraText={undefined}
                shotOverlayPersistentExtraText={formatOverlayShotQuality}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={true}
                aiDrawerMode="split"
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={true}
                isInFullPopup={false}
                renderPlayerIdentity={renderPlayerIdentity}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                onPlaybackShotNumberChange={(shotNumber: number | null) => {
                  setCurrentShotNumber(shotNumber);
                }}
              />
            ) : (
              <Box sx={{ p: 2, color: "rgba(255,255,255,0.75)" }}>
                No rally selected.
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
